import { css, html, LitElement, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import './fields/CheckboxField.js';
import './fields/ChipsField.js';
import './fields/MultiSelectCheckboxField.js';
import './fields/MultiSelectDropdownField.js';
import './fields/MultiSelectField.js';
import './fields/NumberField.js';
import './fields/SelectField.js';
import './fields/TextField.js';

type FieldTemplate = {
  label: string;
  description?: string;
  icon?: string;
  iconColor?: string;
  type: string;
  showSkipFromUI?: boolean;
  showSkipTimes?: boolean;
  options?: Array<string | { value: string; label?: string }>;
  value: any;
  placeholder?: string;
  removable?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  errorMessage?: string;
  requiredMessage?: string;
  errorMessageMulti?: string;
  errorMessageMultiSelect?: string;
};

type FormConfig = {
  templates: Record<string, FieldTemplate>;
  showAddButton?: boolean;
};

export class DynamicForm extends LitElement {
  static styles = css`
    :host {
      display: block;
      padding: 0;
      color: var(--dynamic-form-text-color, #1a202c);
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
        'Helvetica Neue', Arial, sans-serif;
      max-width: 100%;
    }
    .field {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 16px;
      position: relative;
      transition: box-shadow 0.2s;
      animation: fadeIn 0.3s ease-in;
    }
    .field:hover {
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    .field:last-child {
      margin-bottom: 0;
    }
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .field-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;
    }
    .field-header-left {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      min-width: 0;
    }
    .field-icon {
      width: 36px;
      height: 36px;
      flex: 0 0 auto;
      border-radius: 10px;
      background: rgba(79, 70, 229, 0.12);
      color: #4f46e5;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .field-header-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
      align-self: anchor-center;
    }
    .field-title {
      font-size: 14px;
      font-weight: 700;
      color: #0f172a;
      line-height: 1.2;
      display: flex;
      align-items: center;
      min-width: 0;
    }
    .field-subtitle {
      font-size: 12px;
      color: #64748b;
      line-height: 1.3;
    }
    .required-badge {
      color: #ef4444;
      margin-left: 4px;
    }
    .remove-btn {
      background: transparent;
      border: none;
      color: #94a3b8;
      cursor: pointer;
      font-size: 1.2rem;
      padding: 4px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .remove-btn:hover {
      color: #ef4444;
      background: #fee2e2;
    }

    .add-section {
      margin-top: 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      position: relative;
    }
    .add-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }
    .add-label {
      font-size: 13px;
      color: #0f172a;
      font-weight: 500;
      white-space: nowrap;
    }
    .add-section select {
      flex: 1;
      min-width: 240px;
      max-width: 420px;
      padding: 0.5rem 0.75rem;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      background: white;
      color: #0f172a;
      font-size: 13px;
      outline: none;
      transition: all 0.2s;
      cursor: pointer;
    }
    .add-section select:hover {
      border-color: #94a3b8;
    }
    .add-section select:focus {
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .add-section button {
      padding: 0.5rem 1rem;
      background: #e2e8f0;
      color: #475569;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.025em;
      cursor: pointer;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }
    .add-section button:hover:not(:disabled) {
      background: #cbd5e1;
      border-color: #94a3b8;
    }
    .add-section button:active:not(:disabled) {
      background: #cbd5e1;
    }
    .add-section button:disabled {
      background: #e2e8f0;
      color: #94a3b8;
      cursor: not-allowed;
      border-color: #e2e8f0;
    }
    .disabled-msg {
      font-size: 12px;
      color: #64748b;
      width: 100%;
      padding-left: 0.25rem;
    }
    .disabled-msg::before {
      content: '';
    }

    /* Picker popover styles */
    .picker-trigger {
      flex: 1;
      min-width: 240px;
      max-width: 420px;
      padding: 0.5rem 0.75rem;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      background: white;
      color: #0f172a;
      font-size: 13px;
      display: inline-flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      cursor: pointer;
    }
    .picker-trigger:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .picker-panel {
      position: static;
      width: 100%;
      max-width: none;
      border-radius: 10px;
      background: #ffffff;
      border: 1px solid #e6eef6;
      box-shadow: 0 6px 18px rgba(11, 22, 33, 0.04);
      overflow: hidden;
    }
    .picker-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 14px;
      border-bottom: 1px solid #eef4f8;
      background: #fbfdff;
    }
    .picker-title {
      font-size: 12px;
      font-weight: 700;
      color: #6b7280;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .picker-close {
      background: transparent;
      border: none;
      color: #94a3b8;
      cursor: pointer;
      padding: 4px;
    }
    .picker-list {
      max-height: 320px;
      overflow: auto;
      padding: 12px 8px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .picker-item {
      display: flex;
      gap: 14px;
      padding: 12px 14px;
      align-items: center;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.12s, transform 0.06s;
    }
    .picker-item:hover {
      background: #f7fbfd;
      transform: translateY(-1px);
    }
    .picker-item-icon {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(99, 102, 241, 0.08);
      color: #6366f1;
      flex: 0 0 40px;
    }
    .picker-item-title {
      font-size: 14px;
      font-weight: 700;
      color: #0f172a;
    }
    .picker-item-desc {
      font-size: 12px;
      color: #64748b;
    }
    .skip-options {
      display: flex;
      align-items: center;
      gap: 24px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #f1f5f9;
    }
    .skip-checkbox {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: #475569;
    }
    .skip-checkbox label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      user-select: none;
    }
    .skip-times-field {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .skip-times-label {
      font-size: 13px;
      color: #475569;
      font-weight: 500;
      white-space: nowrap;
    }
    .skip-times-input {
      padding: 6px 10px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      width: 80px;
      box-sizing: border-box;
      font-size: 13px;
      color: #0f172a;
      outline: none;
      transition: border-color 0.2s;
      background: transparent;
    }
    .skip-times-input:focus {
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    }
  `;

  @property({ type: Object }) config: FormConfig = { templates: {} };
  @property({ type: Array }) initialVisible?: string[];
  @property({ type: Object }) value?: Record<string, any>;
  @property({ attribute: false }) onChange?: (
    values: Record<string, any>
  ) => void;

  @state() private values: Record<string, any> = {};
  @state() private errors: Record<string, string | undefined> = {};
  @state() private touched: Record<string, boolean> = {};
  @state() private visibleFields: Set<string> = new Set();

  connectedCallback(): void {
    super.connectedCallback();
    this._initFromConfig();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  updated(changed: any) {
    if (changed.has('config') || changed.has('initialVisible')) {
      this._initFromConfig();
    }
    if (changed.has('value') && this.value) {
      this._syncExternalValue();
    }
  }

  private _initFromConfig() {
    const vals: Record<string, any> = {};
    const visible = new Set<string>();

    if (this.config?.templates) {
      Object.entries(this.config.templates).forEach(([key, template]) => {
        // Show fields based on initialVisible prop, or non-removable templates
        if (
          this.initialVisible !== undefined &&
          Array.isArray(this.initialVisible)
        ) {
          if (this.initialVisible.includes(key)) {
            visible.add(key);
          }
        } else if (template.removable === false) {
          visible.add(key);
        }

        // Only create value structure for visible fields
        if (visible.has(key)) {
          let initialValue;
          if (typeof template.value !== 'undefined')
            initialValue = template.value;
          else if (
            template.type === 'multi-select' ||
            template.type === 'multi-select-dropdown' ||
            template.type === 'chips'
          )
            initialValue = [];
          else if (template.type === 'multi-select-checkbox') initialValue = {};
          else if (template.type === 'checkbox')
            initialValue = !!template.value;
          else initialValue = template.value || '';

          vals[key] = { value: initialValue };
        }
      });
    }
    this.values = vals;
    this.visibleFields = visible;
    this.errors = {};
    this.touched = {};
  }

  private _syncExternalValue() {
    if (!this.value) return;

    const newValues: Record<string, any> = {};

    Object.keys(this.value).forEach((key) => {
      const externalField = this.value![key];
      if (externalField && typeof externalField === 'object') {
        newValues[key] = { ...externalField };
      }
    });

    this.values = newValues;
  }

  private _setValue(name: string, v: any) {
    const existingField = this.values[name] || {};

    // Always treat incoming values from field-change events as raw values
    // Field components always emit raw values (string, number, boolean, array)
    // Never wrap objects that are already wrapped
    const updatedField = { ...existingField, value: v };

    this.values = {
      ...this.values,
      [name]: updatedField,
    };
    this._validateFieldByName(name);
    this._emitChange();
  }

  private _handleSkipChange(name: string, skip: boolean) {
    const existingField = this.values[name] || {};
    const updatedField = { ...existingField, skip };
    this.values = {
      ...this.values,
      [name]: updatedField,
    };
    this._emitChange();
  }

  private _handleSkipTimesChange(name: string, skipTimes: number) {
    const existingField = this.values[name] || {};
    const updatedField = { ...existingField, skipTimes };
    this.values = {
      ...this.values,
      [name]: updatedField,
    };
    this._emitChange();
  }

  private _validateFieldByName(name: string) {
    const f = this.config?.templates?.[name];
    if (!f) return;
    const v = this.values[name]?.value;
    if (!f.required) {
      this.errors = { ...this.errors, [name]: undefined };
      return;
    }
    const generic = f.errorMessage || f.requiredMessage || 'Required';
    const multi =
      f.errorMessageMulti ||
      f.errorMessageMultiSelect ||
      generic ||
      'Select at least one';
    let err: string | undefined;
    if (f.type === 'text') {
      if (!v || String(v).trim() === '') err = generic;
    } else if (f.type === 'multi-select' || f.type === 'chips') {
      if (!Array.isArray(v) || v.length === 0) err = multi;
    } else if (f.type === 'multi-select-checkbox') {
      if (
        !v ||
        typeof v !== 'object' ||
        Object.keys(v).filter((k) => v[k]).length === 0
      )
        err = multi;
    } else if (f.type === 'checkbox') {
      if (!v) err = generic;
    } else {
      if (v === undefined || v === null || String(v).trim() === '')
        err = generic;
    }
    this.errors = { ...this.errors, [name]: err };
  }

  private _emitChange() {
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        detail: { value: this.values },
        bubbles: true,
        composed: true,
      })
    );
    if (this.onChange) {
      this.onChange(this.values);
    }
  }

  private _handleRemove(fieldName: string) {
    const visible = new Set(this.visibleFields);
    visible.delete(fieldName);
    this.visibleFields = visible;

    // Remove the value from values object
    const newValues = { ...this.values };
    delete newValues[fieldName];
    this.values = newValues;

    // Remove from errors and touched as well
    const newErrors = { ...this.errors };
    delete newErrors[fieldName];
    this.errors = newErrors;

    const newTouched = { ...this.touched };
    delete newTouched[fieldName];
    this.touched = newTouched;

    this.dispatchEvent(
      new CustomEvent('remove-requirement', {
        detail: { name: fieldName },
        bubbles: true,
        composed: true,
      })
    );
    this._emitChange();
  }

  private _getAvailableTemplates(): Array<[string, FieldTemplate]> {
    if (!this.config?.templates) return [];

    return Object.entries(this.config.templates).filter(([key, template]) => {
      return !this.visibleFields.has(key);
    });
  }

  private _addRequirement(key: string) {
    const visible = new Set(this.visibleFields);
    visible.add(key);
    this.visibleFields = visible;

    // Initialize value for the newly added field
    const template = this.config?.templates?.[key];
    if (template) {
      let initialValue;
      if (typeof template.value !== 'undefined') initialValue = template.value;
      else if (
        template.type === 'multi-select' ||
        template.type === 'multi-select-dropdown' ||
        template.type === 'chips'
      )
        initialValue = [];
      else if (template.type === 'multi-select-checkbox') initialValue = {};
      else if (template.type === 'checkbox') initialValue = !!template.value;
      else initialValue = template.value || '';

      this.values = {
        ...this.values,
        [key]: { value: initialValue },
      };
    }

    this._emitChange();
  }

  private _getIconConfig(key: string, template: FieldTemplate) {
    const name = (template.label || key || '').toLowerCase();
    const color =
      template.iconColor ||
      (name.includes('survey')
        ? '#ef9ff0'
        : name.includes('watch')
        ? '#9fe6c8'
        : name.includes('zbo')
        ? '#ffe4a3'
        : name.includes('public')
        ? '#a2f0e0'
        : name.includes('passcode')
        ? '#fbcfe8'
        : name.includes('location')
        ? '#ffedd5'
        : '#c7d2fe');

    let textColor = '#6366f1';
    let svg = html`
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M9 12l2 2 4-4"></path>
      </svg>
    `;

    if (
      name.includes('verify') ||
      name.includes('attribute') ||
      name.includes('verify user')
    ) {
      textColor = '#2b6cb0';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
          <path d="m9 12 2 2 4-4"></path>
        </svg>
      `;
    } else if (name.includes('passcode') || name.includes('password')) {
      textColor = '#db2777';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
      `;
    } else if (name.includes('location') || name.includes('geo')) {
      textColor = '#ea580c';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
      `;
    } else if (name.includes('survey') || name.includes('complete')) {
      textColor = '#7c3aed';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="16" rx="2"></rect>
          <line x1="8" y1="8" x2="16" y2="8"></line>
          <line x1="8" y1="12" x2="16" y2="12"></line>
        </svg>
      `;
    } else if (name.includes('watch') || name.includes('advert')) {
      textColor = '#16a34a';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polygon points="10 8 16 12 10 16 10 8"></polygon>
        </svg>
      `;
    } else if (name.includes('tag') || name.includes('custom')) {
      textColor = '#475569';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <path
            d="M21 16v-5a2 2 0 0 0-.59-1.42l-9-9a2 2 0 0 0-2.82 0L3 7a2 2 0 0 0 0 2.82l9 9A2 2 0 0 0 14 19h5"></path>
          <circle cx="7.5" cy="7.5" r="1.5"></circle>
        </svg>
      `;
    } else if (
      name.includes('zbo') ||
      name.includes('token') ||
      name.includes('zbo requirement')
    ) {
      textColor = '#b45309';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <circle cx="12" cy="12" r="9"></circle>
          <path d="M12 8v8"></path>
          <path d="M9 11h6"></path>
        </svg>
      `;
    } else if (
      name.includes('public') ||
      name.includes('visibility') ||
      name.includes('service')
    ) {
      textColor = '#059669';
      svg = html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      `;
    }

    return { color, textColor, svg };
  }

  private _renderTemplateIcon(
    key: string,
    template: FieldTemplate
  ): TemplateResult {
    const { color, textColor, svg } = this._getIconConfig(key, template);

    return html`<div
      class="picker-item-icon"
      style="background: ${color}; color: ${textColor};">
      ${svg}
    </div>`;
  }

  private _renderField(key: string, f: FieldTemplate): TemplateResult {
    const label = f.label || key;
    const description = f.description;
    const removable = f.removable !== false;

    const { color, textColor, svg } = this._getIconConfig(key, f);

    const fieldContent = this._renderFieldContent(key, f);

    return html`
      <div class="field">
        <div class="field-header">
          <div class="field-header-left">
            <div
              class="field-icon"
              style="background: ${color}; color: ${textColor};"
              aria-hidden="true">
              ${svg}
            </div>
            <div class="field-header-text">
              <div class="field-title">
                ${label}
                ${f.required ? html`<span class="required-badge">*</span>` : ''}
              </div>
              ${description
                ? html`<div class="field-subtitle">${description}</div>`
                : ''}
            </div>
          </div>
          ${removable
            ? html`
                <button
                  class="remove-btn"
                  @click=${() => this._handleRemove(key)}
                  title="Remove Requirement">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path
                      d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              `
            : ''}
        </div>
        ${fieldContent}
        ${this.touched[key] && this.errors[key]
          ? html`<div class="error">${this.errors[key]}</div>`
          : ''}
        ${f.showSkipFromUI || f.showSkipTimes
          ? html`
              <div class="skip-options">
                ${f.showSkipFromUI
                  ? html`
                      <div class="skip-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            .checked=${this.values[key]?.skip || false}
                            @change=${(e: any) =>
                              this._handleSkipChange(key, e.target.checked)} />
                          Skip from UI
                        </label>
                      </div>
                    `
                  : ''}
                ${f.showSkipTimes
                  ? html`
                      <div class="skip-times-field">
                        <label class="skip-times-label">Skip Times</label>
                        <input
                          class="skip-times-input"
                          type="number"
                          min="0"
                          .value=${this.values[key]?.skipTimes || 0}
                          @input=${(e: any) =>
                            this._handleSkipTimesChange(
                              key,
                              Number(e.target.value)
                            )} />
                      </div>
                    `
                  : ''}
              </div>
            `
          : ''}
      </div>
    `;
  }

  private _renderFieldContent(name: string, f: FieldTemplate): TemplateResult {
    const currentValue = this.values[name]?.value;
    const isTouched = this.touched[name];
    const error = this.errors[name] || '';

    const handleFieldChange = (e: CustomEvent) => {
      this._setValue(e.detail.name, e.detail.value);
    };

    const handleFieldBlur = () => {
      this.touched = { ...this.touched, [name]: true };
      this._validateFieldByName(name);
    };

    if (f.type === 'chips') {
      return html`
        <chips-field
          .name=${name}
          .label=""
          .value=${currentValue}
          .placeholder=${f.placeholder || 'Add value and press Enter...'}
          .required=${!!f.required}
          .error=${error}
          .touched=${isTouched}
          @field-change=${handleFieldChange}
          @field-blur=${handleFieldBlur}>
        </chips-field>
      `;
    }

    if (f.type === 'multi-select') {
      const opts = Array.isArray(f.options) ? f.options : [];
      const options = opts.map((opt) =>
        typeof opt === 'string' ? opt : (opt as any).value
      );

      return html`
        <multi-select-field
          .name=${name}
          .label=""
          .value=${Array.isArray(currentValue) ? currentValue : []}
          .options=${options}
          .required=${!!f.required}
          .error=${error}
          .touched=${isTouched}
          @field-change=${handleFieldChange}
          @field-blur=${handleFieldBlur}>
        </multi-select-field>
      `;
    }

    if (f.type === 'multi-select-dropdown') {
      const opts = Array.isArray(f.options) ? f.options : [];
      const options = opts.map((opt) => {
        if (typeof opt === 'string') {
          return { value: opt, label: opt };
        }
        return {
          value: (opt as any).value,
          label: (opt as any).label || (opt as any).value,
        };
      });

      return html`
        <multi-select-dropdown-field
          .name=${name}
          .label=""
          .value=${Array.isArray(currentValue) ? currentValue : []}
          .options=${options}
          .required=${!!f.required}
          .error=${error}
          .touched=${isTouched}
          @field-change=${handleFieldChange}
          @field-blur=${handleFieldBlur}>
        </multi-select-dropdown-field>
      `;
    }

    if (f.type === 'multi-select-checkbox') {
      const opts = Array.isArray(f.options) ? f.options : [];
      const options = opts.map((opt) => {
        if (typeof opt === 'string') {
          return { value: opt, label: opt };
        }
        return {
          value: (opt as any).value,
          label: (opt as any).label || (opt as any).value,
        };
      });

      return html`
        <multi-select-checkbox-field
          .name=${name}
          .label=""
          .value=${typeof currentValue === 'object' && currentValue !== null
            ? currentValue
            : {}}
          .options=${options}
          .required=${!!f.required}
          .error=${error}
          .touched=${isTouched}
          @field-change=${handleFieldChange}
          @field-blur=${handleFieldBlur}>
        </multi-select-checkbox-field>
      `;
    }

    if (f.type === 'number') {
      return html`
        <number-field
          .name=${name}
          .label=""
          .value=${typeof currentValue === 'number' ? currentValue : 0}
          .placeholder=${f.placeholder || ''}
          .required=${!!f.required}
          .min=${f.min}
          .max=${f.max}
          .step=${f.step}
          .error=${error}
          .touched=${isTouched}
          @field-change=${handleFieldChange}
          @field-blur=${handleFieldBlur}>
        </number-field>
      `;
    }

    if (f.type === 'checkbox') {
      return html`
        <checkbox-field
          .name=${name}
          .label="Enable this option"
          .value=${!!currentValue}
          .required=${!!f.required}
          .error=${error}
          .touched=${isTouched}
          @field-change=${handleFieldChange}
          @field-blur=${handleFieldBlur}>
        </checkbox-field>
      `;
    }

    if (f.type === 'select' || f.type === 'dropdown') {
      const opts = Array.isArray(f.options) ? f.options : [];
      const options = opts.map((opt) => {
        if (typeof opt === 'string') {
          return { value: opt, label: opt };
        }
        return {
          value: (opt as any).value,
          label: (opt as any).label || (opt as any).value,
        };
      });

      return html`
        <select-field
          .name=${name}
          .label=""
          .value=${currentValue || ''}
          .options=${options}
          .placeholder=${f.placeholder || 'Select an option...'}
          .required=${!!f.required}
          .error=${error}
          .touched=${isTouched}
          @field-change=${handleFieldChange}
          @field-blur=${handleFieldBlur}>
        </select-field>
      `;
    }

    // default text
    return html`
      <text-field
        .name=${name}
        .label=""
        .value=${currentValue || ''}
        .placeholder=${f.placeholder || ''}
        .required=${!!f.required}
        .error=${error}
        .touched=${isTouched}
        @field-change=${handleFieldChange}
        @field-blur=${handleFieldBlur}>
      </text-field>
    `;
  }

  render() {
    const templates = this.config?.templates || {};
    const visibleEntries = Object.entries(templates).filter(([key]) =>
      this.visibleFields.has(key)
    );
    const showAddButton =
      this.config?.showAddButton !== false && this.config?.templates;
    const availableTemplates = showAddButton
      ? this._getAvailableTemplates()
      : [];

    return html`
      ${visibleEntries.length === 0
        ? html`<div>No fields configured</div>`
        : visibleEntries.map(([key, template]) =>
            this._renderField(key, template)
          )}
      ${showAddButton
        ? html`
            <div class="add-section">
              ${availableTemplates.length > 0
                ? html`
                    <div
                      class="picker-panel"
                      role="dialog"
                      aria-label="Select requirement type">
                      <div class="picker-header">
                        <div class="picker-title">SELECT REQUIREMENT TYPE</div>
                      </div>
                      <div class="picker-list">
                        ${availableTemplates.map(
                          ([key, template]) => html`
                            <div
                              class="picker-item"
                              @click=${() => this._addRequirement(key)}>
                              ${this._renderTemplateIcon(key, template)}
                              <div>
                                <div class="picker-item-title">
                                  ${template.label}
                                </div>
                                <div class="picker-item-desc">
                                  ${template.description || ''}
                                </div>
                              </div>
                            </div>
                          `
                        )}
                      </div>
                    </div>
                  `
                : html`
                    <div class="disabled-msg">All requirements added.</div>
                  `}
            </div>
          `
        : ''}
    `;
  }
}

declare global {
  interface Window {
    DynamicForm?: typeof DynamicForm;
  }
  interface HTMLElementTagNameMap {
    'dynamic-form': DynamicForm;
  }
}

if (!customElements.get('dynamic-form')) {
  customElements.define('dynamic-form', DynamicForm);
}
